import React, {useState, useEffect, Fragment} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import Show from './show';

const nationality = [
  {
    label: '本國人',
    value: 0,
  },
  {
    label: '外籍',
    value: 1,
  },
];

const insuranceIdentity = [
  {
    label: '雇主',
    value: '0',
  },
  {
    label: '一般',
    value: '1',
  },
];

const deductionType = [
  {
    label: '補助費率',
    value: 0,
  },
  {
    label: '最高補助金額',
    value: 1,
  },
];

const yesorno = [
  {
    label: '是',
    value: true,
  },
  {
    label: '否',
    value: false,
  },
];

const NhiData = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    onBoardingDate,
    getInsuranceAmount,
    updateLocale,
    defaultEmployeeId,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, errors, setValue, trigger} = formProps;
  const isUnable = !useWatch({
    control,
    name: `healthInsurance.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isDeduction = useWatch({
    control,
    name: `healthInsurance.isDeduction`,
    defaultValue: defaultValues.isDeduction,
  });
  const deductionRateType = useWatch({
    control,
    name: `healthInsurance.deductionRateType`,
    defaultValue: defaultValues.deductionRateType,
  });
  const effectiveDate = useWatch({
    control,
    name: `healthInsurance.effectiveDate`,
    defaultValue: defaultValues.effectiveDate,
  });
  const employeeId = useWatch({
    control,
    name: `employee.id`,
    defaultValue: defaultEmployeeId,
  });
  const [insuranceAmount, setInsuranceAmount] = useState([]);
  const [isInsuranceAmountFetching, setIsInsuranceAmountFetching] =
    useState(false);
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);

  useEffect(() => {
    if (defaultValues.effectiveDate && employeeId) {
      setIsInsuranceAmountFetching(true);
      getInsuranceAmount({
        amountLevelType: 2,
        insuranceAmountLevelId: undefined,
        effectiveDate: defaultValues.effectiveDate,
        insuranceIdentityId: undefined,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  }, [defaultValues.effectiveDate, employeeId]);

  const updateInsuranceAmount = ({
    effectiveDateInput,
  }: {
    effectiveDateInput?: string;
  }) => {
    if (effectiveDateInput || effectiveDate) {
      setIsInsuranceAmountFetching(true);
      setInsuranceAmount([]);
      getInsuranceAmount({
        amountLevelType: 2,
        insuranceAmountLevelId: undefined,
        effectiveDate: effectiveDateInput || effectiveDate,
        insuranceIdentityId: undefined,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  };

  return (
    <div data-test-id={'nhi-data'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>健保</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-health-insurance'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(enabled: Boolean) => {
                  onChange(enabled);
                  if (!enabled) {
                    setValue(`dependentsHealthInsurance.enabled`, false);
                  }
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`healthInsurance.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <Show isShow>
          <FormItem
            label="生效日期"
            itemName="nhi_effectiveDate"
            required
            width="160px"
            data-test-id={'effective-date'}
          >
            <Controller
              render={({onChange, value}) => (
                <Fragment>
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(onBoardingDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || isUnable}
                    onChange={(date: Date) => {
                      const dateString = formatYYYYMMDD(date);
                      onChange(dateString);
                      setValue(`healthInsurance.insuranceAmountLevelId`, null);
                      updateInsuranceAmount({effectiveDateInput: dateString});
                      updateLocale();
                    }}
                    onBlur={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    onCalendarClose={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    error={isError(`healthInsurance.effectiveDate`)}
                  />
                  {renderErrorMessage(`laborInsurance.effectiveDate`)}
                </Fragment>
              )}
              name={`healthInsurance.effectiveDate`}
              control={control}
              rules={{
                required: !isUnable && '生效日期為必填',
                validate: {
                  isAfterOnBoardingDate: v => {
                    let onBoardingDateObj = transferDateObj(onBoardingDate);
                    let effectiveDateObj = transferDateObj(v);
                    if (!isUnable && onBoardingDateObj && effectiveDateObj) {
                      return (
                        DateTime.fromJSDate(onBoardingDateObj) <=
                          DateTime.fromJSDate(effectiveDateObj) ||
                        `不可早於到職日${onBoardingDate}`
                      );
                    }
                    return true;
                  },
                },
              }}
              defaultValue={defaultValues.effectiveDate}
            />
          </FormItem>
          <FormItem
            label="籍別"
            itemName="nhi_nationality"
            required
            width="100px"
            data-test-id={'nationality'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={nationality.find(o => o.value === value)}
                  options={nationality}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`healthInsurance.nationality`}
              control={control}
              defaultValue={defaultValues.nationality}
            />
          </FormItem>
          <FormItem
            label="投保單位"
            itemName="nhi_insuranceApplicantUnitId"
            required
            width="200px"
            data-test-id={'insurance-applicant-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceUnits.find(
                      (o: any) => o.insuranceApplicantUnitId === value,
                    ) || null
                  }
                  options={options.insuranceUnits}
                  onChange={(o: any) => {
                    onChange(o.insuranceApplicantUnitId);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) =>
                    `${o.nHIUnitCode} ${o.insuranceApplicantUnitName}`
                  }
                  getOptionValue={(o: any) => o.insuranceApplicantUnitId}
                  error={isError(`healthInsurance.insuranceApplicantUnitId`)}
                />
              )}
              name={`healthInsurance.insuranceApplicantUnitId`}
              control={control}
              rules={{required: !isUnable && '投保單位為必填'}}
              defaultValue={defaultValues.insuranceApplicantUnitId}
            />
          </FormItem>
          <FormItem
            label="投保金額"
            itemName="nhi_insuranceAmountLevelId"
            required
            width="135px"
            data-test-id={'insurance-amount-level-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  isLoading={isInsuranceAmountFetching}
                  loadingMessage={() => '載入中...'}
                  value={
                    insuranceAmount.find((o: any) => o.value === value) || ''
                  }
                  options={insuranceAmount}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  onMenuOpen={() => {
                    if (insuranceAmount.length === 0) {
                      updateInsuranceAmount({});
                    }
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(`healthInsurance.insuranceAmountLevelId`)}
                />
              )}
              name={`healthInsurance.insuranceAmountLevelId`}
              control={control}
              rules={{required: !isUnable && '投保金額為必填'}}
              defaultValue={defaultValues.insuranceAmountLevelId}
            />
          </FormItem>
          <FormItem
            label="投保身分"
            itemName="nhi_insuranceIdentity"
            required
            width="100px"
            data-test-id={'insurance-identity'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={insuranceIdentity.find(o => o.value === value)}
                  options={insuranceIdentity}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`healthInsurance.insuranceIdentity`}
              control={control}
              defaultValue={defaultValues.insuranceIdentity}
            />
          </FormItem>
          <FormItem
            label="減免身分"
            itemName="nhi_isDeduction"
            required
            width="80px"
            data-test-id={'is-deduction'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={yesorno.find(o => o.value === value)}
                  options={yesorno}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`healthInsurance.isDeduction`}
              control={control}
              defaultValue={defaultValues.isDeduction}
            />
          </FormItem>
          <FormItem
            label="減免類型"
            itemName="nhi_deductionRateType"
            required
            width="135px"
            hidden={!isDeduction}
            data-test-id={'deduction-rate-type'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={deductionType.find((o: any) => o.value === value)}
                  options={deductionType}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`healthInsurance.deductionRateType`}
              control={control}
              defaultValue={defaultValues.deductionRateType}
            />
          </FormItem>
          <FormItem
            label="補助費率"
            itemName="nhi_allowanceRate"
            required
            width="100px"
            hidden={!isDeduction || deductionRateType !== 0}
            data-test-id={'allowance-rate'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (
                      !value ||
                      /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/.test(value)
                    ) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  error={
                    deductionRateType === 0 &&
                    isDeduction &&
                    isError(`healthInsurance.allowanceRate`)
                  }
                />
              )}
              name={`healthInsurance.allowanceRate`}
              control={control}
              rules={{
                required:
                  deductionRateType === 0 &&
                  isDeduction &&
                  !isUnable &&
                  '補助費率為必填',
                min: {
                  value: 0,
                  message: '最小值為0',
                },
                max: {
                  value: 100,
                  message: '最大值為100',
                },
              }}
              defaultValue={defaultValues.allowanceRate}
            />
            {renderErrorMessage(`healthInsurance.allowanceRate`)}
          </FormItem>
          <FormItem
            label="最高補助金額"
            itemName="nhi_highestAllowanceAmount"
            required
            width="120px"
            hidden={!isDeduction || deductionRateType !== 1}
            data-test-id={'highest-allowance-amount'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (!value || /\d/.test(value)) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  maxLength={9}
                  error={
                    deductionRateType === 1 &&
                    isDeduction &&
                    isError(`healthInsurance.highestAllowanceAmount`)
                  }
                />
              )}
              name={`healthInsurance.highestAllowanceAmount`}
              control={control}
              rules={{
                required:
                  deductionRateType === 1 &&
                  isDeduction &&
                  !isUnable &&
                  '最高補助金額為必填',
              }}
              defaultValue={defaultValues.highestAllowanceAmount}
            />
            {renderErrorMessage(`healthInsurance.highestAllowanceAmount`)}
          </FormItem>
          <FormItem
            label="減免備註"
            itemName="nhi_allowanceRemark"
            width="135px"
            hidden={!isDeduction}
            data-test-id={'allowance-remark'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={(e: any) => {
                    onChange(e);
                    updateLocale();
                  }}
                  maxLength={250}
                />
              )}
              name={`healthInsurance.allowanceRemark`}
              control={control}
              defaultValue={defaultValues.allowanceRemark}
            />
          </FormItem>
        </Show>
      </Block>
    </div>
  );
};

export default NhiData;
